<template>
    <div class="project-create-chat flex h-[calc(100vh-124px)]  justify-between" v-show="copilotDone">
        <ProjectIcon ref="projectIconRef" v-if="currentProject" class="hidden shrink-0" :icon="currentProject.icon" size="md" />
        <ChatWithCopilot class=" w-full grow bg-grey-10 py-5" />
        <Step :key="`${route.params?.projectId}-${route.query?.ts}`" ref="stepRef" class="shrink-0 lg:w-[350px] xl:w-[470px]" />
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch, nextTick, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';
import ChatWithCopilot from './ChatWithCopilot.vue';
import Step from './Step.vue';
import type { ProjectCopilotServiceImpl } from '@/services/project-copilot-service';
import { PatsnapCopilotService } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';
import { WuJieBus } from '@analytics/patsnap-copilot/src/wujie-bus';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { LocalStorageService } from '@eureka/services/src/storage-service'
import { useI18n } from 'vue-i18n';
import { getProjectInfo } from '@/infrastructure/api-functions/report';
import ProjectIcon from '@/components/ProjectIcon.vue'

import type { IProject } from '@/types';
import _ from 'lodash'
import '@/assets/icons/copilot-white.svg';
import '@patsnap-ui/icon/assets/solid/ArrowLeft.svg';

const wujieBus = injectService<WuJieBus>(WuJieBus);
const copilotService = injectService<PatsnapCopilotService>(PatsnapCopilotService) as unknown as ProjectCopilotServiceImpl;

const { t } = useI18n();

const copilotDone = ref(false)

const stepRef = ref()
const route = useRoute();
const router = useRouter();
const currentProject = ref<Nullable<IProject>>(null)
const projectIconRef = ref(null);

const getCurrentProject = function() {
    return getProjectInfo(route.params.projectId);
}

function setInputDisabled(v: boolean) {
    wujieBus.$emit('copilot-input-disabled', v);
}

function handleSendTextFromOther() {
    // 处理缓存的 question_id 参数
    const question_id = route.query.question_id as string;
    if (question_id) {
        const cacheQuestionText = LocalStorageService.get(question_id);
        wujieBus.$emit('send-message', {
            text: cacheQuestionText,
        })
    }
}

async function handleCopilotLoaded() {
    copilotDone.value = true;

    if (!copilotService.jobId) {
        setInputDisabled(false);
        copilotService.newSession();
        handleSendTextFromOther();
    } else {
        setInputDisabled(true);
        const current = await getCurrentProject();
        currentProject.value = current;
        await nextTick();
        const imgSrc = projectIconRef.value?.$el?.getAttribute('src');
        copilotService.newSession({
            text: null,
            type: 'BoardFinish',
            data: {
                icon: `<img style="width: 64px" src="${imgSrc}" />`,
                title: current.project_title,
                date: current.created_at ? dayjs(current.created_at).format('YYYY-MM-DD') : '',
                onClick: async() => {
                    await router.push({
                        name: 'report',
                        params: {
                            projectId: current.project_id,
                        },
                    })
                    wujieBus.$emit('clean-message', true);
                },
            },
        });
    }
}

watch(route, async({ params }) => {
    copilotService.setJobId(params.projectId as string);
    stepRef?.value?.updateStatus(copilotService.jobId);
    handleCopilotLoaded();
}, { immediate: true, deep: true })

copilotService.setDefaultMessage({
    text: null,
    type: 'ProjectWelcome',
    data: {
        title: t('chat.welcome'),
        text: t('chat.example'),
    },
})

copilotService.onGenerate(() => {
    setInputDisabled(true);
    return stepRef.value.analysis(copilotService.jobId).then(() => {
        wujieBus.$emit('send-message', {
            text: null,
            type: 'plain_text',
            data: {
                content: t('chat.confirm'),
            },
        });
    }).catch(e => {
        setInputDisabled(false);
    });
})

onMounted(() => {
    stepRef?.value?.updateStatus(copilotService.jobId);
})

function copilotBusyChange(isBusy: boolean, turns: any[]) {
    if (!isBusy) {
        const lastTurn = _.last(turns);
        copilotService.lastInput = lastTurn?.input?.text || '';
        if (_.get(lastTurn, 'reply.detail.message.current_question')) {
            stepRef.value.updateStatus(copilotService.jobId, lastTurn.reply.detail.message.current_question);
        }
    }
}
wujieBus.$on('copilot-busy-change', copilotBusyChange);
wujieBus.$on('copilot-loaded', handleCopilotLoaded)

onBeforeUnmount(() => {
    wujieBus.$off('copilot-busy-change', copilotBusyChange);
    wujieBus.$off('copilot-loaded', handleCopilotLoaded);

    const questionId = route.query.question_id as string;
    if (questionId) {
        LocalStorageService.delete(questionId);
    }
})
</script>
